/* Fully-automated "Topup Jeux" client view.

   Flow (per latest product spec):
   1. Client picks a game and types the USD amount they want to recharge.
   2. System auto-computes: subtotal + 1% service fee = total.
   3. Account email/ID + password fields appear under the form.
   4. Client clicks "Payer avec mon solde".
   5. Backend debits balance, creates a `pending` order, returns an admin
      WhatsApp URL — we auto-open it so the admin is notified instantly.
   6. Admin marks "Terminé" from the dashboard.
*/
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Gamepad2, Plus, Clock, CheckCircle, X, AlertCircle, RefreshCw,
  Wallet, Eye, EyeOff, Send,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Dynamic API base — must match ClientDashboard so production and preview both work.
// Without this, on https://smartechsystempro.com we'd hit the preview backend
// (different DB) → 401 "Client non trouvé" when the JWT sub references a prod
// user that doesn't exist in preview.
const getApiUrl = () => {
  if (typeof window === "undefined") return BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === "smartechsystempro.com" || hostname === "www.smartechsystempro.com") return `${protocol}//${hostname}/api`;
  if (hostname.includes("emergent.host") || hostname.includes("emergentagent.com")) return `${protocol}//${hostname}/api`;
  return BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
};
const API = getApiUrl();
const FEE_RATE = 0.01; // 1%

const headers = () => {
  const t = localStorage.getItem("client_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS = {
  // New instant flow
  pending:        { label: "En attente",            color: "#92400E", bg: "#FEF3C7" },
  completed:      { label: "Terminé",               color: "#15803D", bg: "#DCFCE7" },
  // Legacy flow (kept for backwards compat with older orders)
  awaiting_quote: { label: "En attente de devis",   color: "#A16207", bg: "#FEF3C7" },
  quoted:         { label: "À payer",               color: "#1D4ED8", bg: "#DBEAFE" },
  paid:           { label: "Payé — En traitement",  color: "#7C3AED", bg: "#EDE9FE" },
  validated:      { label: "Livré ✓",               color: "#15803D", bg: "#DCFCE7" },
  rejected:       { label: "Rejeté",                color: "#B91C1C", bg: "#FEE2E2" },
};

const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function ClientGameTopup({ clientBalanceHtg, exchangeRate, onBalanceChange }) {
  const [games, setGames] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // Create form (instant flow)
  const [gameId, setGameId] = useState("");
  const [amountUsd, setAmountUsd] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accountPwd, setAccountPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [g, o] = await Promise.all([
        axios.get(`${API}/game-topup/games`).catch(() => ({ data: { games: [] } })),
        axios.get(`${API}/client/game-topup/orders`, { headers: headers() }).catch(() => ({ data: { orders: [] } })),
      ]);
      setGames(g.data.games || []);
      setOrders(o.data.orders || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rate = Number(exchangeRate || 147);
  const clientBalanceUsd = Number(clientBalanceHtg || 0) / (rate || 1);

  const amt = useMemo(() => {
    const n = parseFloat(amountUsd);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [amountUsd]);
  const fee = useMemo(() => Math.round(amt * FEE_RATE * 100) / 100, [amt]);
  const total = useMemo(() => Math.round((amt + fee) * 100) / 100, [amt, fee]);
  const insufficient = total > 0 && total > clientBalanceUsd;
  // Show credentials once the amount is valid (per product spec: "Après avoir saisi le montant").
  const showCredsForm = amt > 0;

  const resetForm = () => {
    setGameId(""); setAmountUsd(""); setAccountId(""); setAccountPwd("");
    setShowPwd(false); setNote("");
  };

  const submit = async () => {
    if (!gameId) { toast.error("Choisissez un jeu"); return; }
    if (amt <= 0) { toast.error("Saisissez un montant valide"); return; }
    if (!accountId.trim()) { toast.error("Email ou ID du compte requis"); return; }
    if (!accountPwd.trim()) { toast.error("Mot de passe requis"); return; }
    if (insufficient) {
      toast.error(`Solde insuffisant. Solde actuel : ${fmtUSD(clientBalanceUsd)}. Total requis : ${fmtUSD(total)}.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/client/game-topup/instant-order`,
        {
          game_id: gameId,
          amount_usd: amt,
          account_id: accountId.trim(),
          account_password: accountPwd.trim(),
          note: note || undefined,
        },
        { headers: headers() },
      );
      toast.success("Paiement effectué. Notification envoyée à l'admin.", { duration: 4000 });
      if (onBalanceChange) onBalanceChange(Number(res.data?.new_balance_htg) || 0);
      // Auto-open admin WhatsApp notification.
      const waUrl = res.data?.admin_whatsapp_url;
      if (waUrl) {
        try { window.open(waUrl, "_blank", "noopener,noreferrer"); } catch { /* ignore */ }
      }
      setShowCreate(false); resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="client-game-topup">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: "0.85rem" }}>
        <h3 style={{ margin: 0, fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 6 }}>
          <Gamepad2 size={20} color="#7C3AED" /> Topup Jeux
        </h3>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={fetchData} className="btn btn-sm" style={{ background: "#E2E8F0", color: "#0F172A" }} data-testid="gt-refresh">
            <RefreshCw size={12} />
          </button>
          <button onClick={() => setShowCreate(true)} className="btn btn-sm" style={{ background: "#7C3AED", color: "white", fontWeight: 700 }} data-testid="gt-create-btn">
            <Plus size={13} /> Nouvelle commande
          </button>
        </div>
      </div>

      {/* Info banner (USD-only) */}
      <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "0.55rem 0.7rem", marginBottom: "0.7rem", fontSize: "0.75rem", color: "#1E3A8A" }}>
        💡 Saisissez le montant en dollars que vous souhaitez recharger. Frais de service : <b>1%</b>. Paiement instantané depuis votre solde.
      </div>

      {loading && <div style={{ textAlign: "center", padding: "1.5rem", color: "#64748B" }}>Chargement…</div>}

      {!loading && orders.length === 0 && (
        <div style={{ textAlign: "center", padding: "1.5rem", color: "#64748B", background: "#F8FAFC", borderRadius: 8 }} data-testid="gt-empty">
          Aucune commande pour le moment. Cliquez sur « Nouvelle commande » pour commencer.
        </div>
      )}

      {orders.map(o => {
        const s = STATUS[o.status] || { label: o.status, color: "#475569", bg: "#F1F5F9" };
        const isInstant = o.is_instant === true;
        const displayTotalUsd = isInstant ? Number(o.total_usd || 0) : Number(o.in_game_price_usd || 0);
        const subtitle = isInstant
          ? `Recharge ${fmtUSD(o.amount_usd)} + frais 1% ${fmtUSD(o.service_fee_usd)}`
          : (o.request_text || "");
        return (
          <div key={o.id} data-testid={`gt-order-${o.id}`} style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: 10, padding: "0.8rem", marginBottom: "0.55rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
              <div style={{ fontWeight: 700 }}>
                <span style={{ marginRight: 4 }}>{o.game_icon}</span>{o.game_name}
                {isInstant && <span style={{ color: "#7C3AED", marginLeft: 6, fontWeight: 800 }}>· {fmtUSD(displayTotalUsd)}</span>}
              </div>
              <span style={{ background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 700 }}>{s.label}</span>
            </div>
            <div style={{ fontSize: "0.74rem", color: "#475569", marginTop: 4 }}>{subtitle}</div>
            <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
              {new Date(o.created_at).toLocaleString("fr-FR")}
            </div>

            {/* Legacy "quoted" path (kept for older orders) */}
            {o.status === "quoted" && (
              <div style={{ background: "#FFFBEB", padding: "0.6rem", borderRadius: 8, marginTop: 6, fontSize: "0.85rem", color: "#92400E" }}>
                💰 Ancienne commande en attente — basculée vers le mode automatisé.
              </div>
            )}
            {o.status === "pending" && (
              <div style={{ background: "#FEF3C7", padding: "0.45rem 0.6rem", borderRadius: 6, marginTop: 6, fontSize: "0.78rem", color: "#92400E" }}>
                <Clock size={12} /> En attente — l&apos;admin traite votre recharge.
              </div>
            )}
            {o.status === "completed" && (
              <div style={{ background: "#DCFCE7", padding: "0.4rem 0.6rem", borderRadius: 6, marginTop: 6, fontSize: "0.78rem", color: "#15803D" }}>
                <CheckCircle size={12} /> Recharge effectuée — Merci !
              </div>
            )}
            {o.status === "validated" && (
              <div style={{ background: "#DCFCE7", padding: "0.4rem 0.6rem", borderRadius: 6, marginTop: 6, fontSize: "0.78rem", color: "#15803D" }}>
                <CheckCircle size={12} /> Commande livrée — Merci !
              </div>
            )}
            {o.status === "rejected" && o.reject_reason && (
              <div style={{ background: "#FEE2E2", padding: "0.4rem 0.6rem", borderRadius: 6, marginTop: 6, fontSize: "0.72rem", color: "#991B1B" }}>
                ❌ {o.reject_reason}
              </div>
            )}
          </div>
        );
      })}

      {/* Create modal */}
      {showCreate && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={() => { setShowCreate(false); resetForm(); }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: 12, padding: "1rem", maxWidth: 480, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
            data-testid="gt-create-modal"
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.7rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Nouvelle commande Topup Jeu</h3>
              <button onClick={() => { setShowCreate(false); resetForm(); }} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="gt-create-close"><X size={18} /></button>
            </div>

            <div style={{
              background: "#F1F5F9", padding: "0.45rem 0.65rem", borderRadius: 8,
              fontSize: "0.78rem", color: "#334155", display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 10,
            }}>
              <span><Wallet size={12} /> Solde dispo</span>
              <strong style={{ color: "#0F172A" }}>{fmtUSD(clientBalanceUsd)}</strong>
            </div>

            <label style={lblStyle}>Choisissez le jeu</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 6, marginBottom: 10 }}>
              {games.map(g => (
                <button
                  key={g.id}
                  onClick={() => setGameId(g.id)}
                  data-testid={`gt-pick-${g.slug || g.id}`}
                  style={{
                    background: gameId === g.id ? "#7C3AED" : "#F1F5F9",
                    color: gameId === g.id ? "white" : "#0F172A",
                    border: "1px solid " + (gameId === g.id ? "#7C3AED" : "#E2E8F0"),
                    padding: "0.45rem", borderRadius: 8, cursor: "pointer",
                    fontSize: "0.78rem", fontWeight: 600,
                  }}
                >
                  <div style={{ fontSize: "1.2rem" }}>{g.icon}</div>
                  {g.name}
                </button>
              ))}
            </div>

            <label style={lblStyle}>Montant à recharger (USD)</label>
            <input
              type="number" step="0.01" min="0.01"
              value={amountUsd}
              onChange={e => setAmountUsd(e.target.value)}
              placeholder="Ex: 100"
              data-testid="gt-amount-usd"
              style={inputStyle}
              autoComplete="off"
            />

            {amt > 0 && (
              <div
                data-testid="gt-total-preview"
                style={{
                  background: "#F0FDF4", border: "1px solid #BBF7D0", padding: "0.55rem 0.65rem",
                  borderRadius: 8, marginTop: 6, marginBottom: 8, fontSize: "0.82rem", color: "#166534",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Montant saisi</span><strong>{fmtUSD(amt)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Frais (1%)</span><strong>{fmtUSD(fee)}</strong>
                </div>
                <hr style={{ border: "none", borderTop: "1px solid #BBF7D0", margin: "4px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800 }}>
                  <span>Total à payer</span><strong>{fmtUSD(total)}</strong>
                </div>
                {insufficient && (
                  <div style={{ marginTop: 4, color: "#B91C1C", fontWeight: 700 }}>
                    <AlertCircle size={12} /> Solde insuffisant.
                  </div>
                )}
              </div>
            )}

            {showCredsForm && (
              <div data-testid="gt-credentials-form" style={{ marginTop: 4 }}>
                <label style={lblStyle}>Email ou ID du compte de jeu</label>
                <input
                  type="text"
                  value={accountId}
                  onChange={e => setAccountId(e.target.value)}
                  placeholder="Ex: monemail@gmail.com ou 1234567890"
                  data-testid="gt-account-id"
                  style={inputStyle}
                  autoComplete="off"
                  maxLength={200}
                />

                <label style={lblStyle}>Mot de passe du compte</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPwd ? "text" : "password"}
                    value={accountPwd}
                    onChange={e => setAccountPwd(e.target.value)}
                    placeholder="••••••••"
                    data-testid="gt-account-password"
                    style={{ ...inputStyle, paddingRight: 36 }}
                    autoComplete="off"
                    maxLength={200}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(p => !p)}
                    data-testid="gt-toggle-pwd"
                    style={{
                      position: "absolute", right: 6, top: 6,
                      background: "transparent", border: "none", cursor: "pointer", color: "#64748B",
                    }}
                  >{showPwd ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>

                <label style={lblStyle}>Note (optionnel)</label>
                <input
                  type="text" value={note} onChange={e => setNote(e.target.value)}
                  maxLength={200} data-testid="gt-note" style={inputStyle}
                />

                <button
                  onClick={submit}
                  disabled={submitting || insufficient}
                  data-testid="gt-submit-pay"
                  style={{
                    width: "100%",
                    background: submitting || insufficient ? "#94A3B8" : "#16A34A",
                    color: "white", padding: "0.65rem",
                    border: "none", borderRadius: 8,
                    fontWeight: 800, fontSize: "0.9rem",
                    cursor: submitting || insufficient ? "not-allowed" : "pointer",
                    marginTop: 10, display: "inline-flex",
                    alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                >
                  <Send size={14} /> {submitting ? "Paiement…" : `Payer avec mon solde · ${fmtUSD(total)}`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const lblStyle = { fontSize: "0.72rem", color: "#475569", fontWeight: 700, display: "block", marginTop: 6 };
const inputStyle = { width: "100%", padding: "0.5rem", borderRadius: 6, border: "1px solid #CBD5E1", marginTop: 3, marginBottom: 4, fontSize: "0.9rem", outline: "none" };
